import { ref } from 'vue';
import whatsappCalls from 'dashboard/api/whatsappCalls';

// MODULE-SCOPE singleton: Button (startCall) and Panel (applyAnswer / toggleMute / hangup)
// must share ONE RTCPeerConnection. Placing pc / localStream / isMuted here ensures every
// useWhatsappCall() call-site closes over the same state rather than creating a fresh one.
let pc = null;
let localStream = null;
let remoteAudio = null;
const isMuted = ref(false);

// The inbound audio track must be attached to a media element or it is never
// heard. We manage a single hidden <audio> element for the lifetime of a call.
const ensureRemoteAudio = () => {
  if (remoteAudio) return remoteAudio;
  remoteAudio = document.createElement('audio');
  remoteAudio.autoplay = true;
  remoteAudio.setAttribute('playsinline', '');
  remoteAudio.style.display = 'none';
  document.body.appendChild(remoteAudio);
  return remoteAudio;
};

const teardownRemoteAudio = () => {
  if (!remoteAudio) return;
  remoteAudio.srcObject = null;
  remoteAudio.remove();
  remoteAudio = null;
};

// --- browser-side recording: mix local mic + remote audio → MediaRecorder ---
// (Official Meta calls relay audio via Meta, not our server, so the only place
// to record is the agent's browser. Uploaded on cleanup.)
let mediaRecorder = null;
let recordedChunks = [];
let recordCtx = null;
let remoteStream = null;
let recordingMeta = { conversationId: null, recordId: null };

const startRecording = () => {
  if (mediaRecorder || !localStream || !remoteStream) return;
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC || typeof MediaRecorder === 'undefined') return;
    recordCtx = new AC();
    const dest = recordCtx.createMediaStreamDestination();
    recordCtx.createMediaStreamSource(localStream).connect(dest);
    recordCtx.createMediaStreamSource(remoteStream).connect(dest);
    recordedChunks = [];
    const mime = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
      ? 'audio/webm;codecs=opus'
      : 'audio/webm';
    mediaRecorder = new MediaRecorder(dest.stream, { mimeType: mime });
    mediaRecorder.addEventListener('dataavailable', e => {
      if (e.data && e.data.size > 0) recordedChunks.push(e.data);
    });
    mediaRecorder.start();
  } catch {
    mediaRecorder = null;
  }
};

const stopRecordingAndUpload = () => {
  const rec = mediaRecorder;
  const meta = { ...recordingMeta };
  mediaRecorder = null;
  if (!rec || rec.state === 'inactive') {
    recordCtx?.close?.();
    recordCtx = null;
    return;
  }
  rec.addEventListener('stop', () => {
    try {
      const blob = new Blob(recordedChunks, { type: 'audio/webm' });
      recordedChunks = [];
      recordCtx?.close?.();
      recordCtx = null;
      if (blob.size > 0 && meta.conversationId && meta.recordId) {
        whatsappCalls
          .uploadRecording(meta.conversationId, meta.recordId, blob)
          .catch(() => {});
      }
    } catch {
      /* ignore */
    }
  });
  try {
    rec.stop();
  } catch {
    /* ignore */
  }
};

const waitForIceGathering = localPc =>
  new Promise(resolve => {
    if (localPc.iceGatheringState === 'complete') {
      resolve();
      return;
    }
    // Capture timer so the check listener can cancel it before it fires.
    const timer = setTimeout(resolve, 3000);
    const check = () => {
      if (localPc.iceGatheringState === 'complete') {
        clearTimeout(timer);
        localPc.removeEventListener?.('icegatheringstatechange', check);
        resolve();
      }
    };
    localPc.addEventListener('icegatheringstatechange', check);
  });

// Attach the three listeners that both startCall (outbound) and answerCall
// (inbound) need: remote-track routing to the hidden audio element, plus
// ICE/connection state diagnostics.
const attachPcListeners = (peerConn, audioEl) => {
  peerConn.addEventListener('track', event => {
    const [stream] = event.streams;
    remoteStream = stream || new MediaStream([event.track]);
    audioEl.srcObject = remoteStream;
    audioEl.play?.().catch(() => {});
    startRecording();
  });
  // Diagnostics: surface the media-path state so a dead call is debuggable.
  peerConn.addEventListener('iceconnectionstatechange', () => {
    // eslint-disable-next-line no-console
    console.info('[wa-call] iceConnectionState:', peerConn?.iceConnectionState);
  });
  peerConn.addEventListener('connectionstatechange', () => {
    // eslint-disable-next-line no-console
    console.info('[wa-call] connectionState:', peerConn?.connectionState);
  });
};

export function useWhatsappCall() {
  const cleanup = () => {
    stopRecordingAndUpload();
    localStream?.getTracks().forEach(t => t.stop());
    pc?.close();
    pc = null;
    localStream = null;
    remoteStream = null;
    isMuted.value = false;
    teardownRemoteAudio();
  };

  const startCall = async conversationId => {
    // Re-entrancy guard: if a previous call was orphaned, tear it down first.
    if (pc) cleanup();

    const { data: ice } = await whatsappCalls.iceServers(conversationId);
    pc = new RTCPeerConnection({ iceServers: ice.ice_servers });

    attachPcListeners(pc, ensureRemoteAudio());

    localStream = await navigator.mediaDevices.getUserMedia({ audio: true });
    localStream.getTracks().forEach(t => pc.addTrack(t, localStream));
    const offer = await pc.createOffer({ offerToReceiveAudio: true });
    await pc.setLocalDescription(offer);
    await waitForIceGathering(pc);
    try {
      const { data } = await whatsappCalls.initiate(
        conversationId,
        pc.localDescription.sdp
      );
      // recordId = DB primary key, used for terminate (wa_call_id can contain
      // a '.', which breaks Rails route :id parsing).
      recordingMeta = { conversationId, recordId: data.id };
      return {
        callId: data.wa_call_id,
        recordId: data.id,
        status: data.status,
      };
    } catch (e) {
      cleanup();
      const callError = new Error(e.response?.data?.error || 'Call failed');
      callError.errorCode = e.response?.data?.error_code;
      throw callError;
    }
  };

  // answerCall: inbound mirror of startCall. We receive the caller's offer,
  // create a local answer, gather ICE, and post the answer SDP to the server.
  const answerCall = async (conversationId, recordId, offerSdp) => {
    if (pc) cleanup();
    const { data: ice } = await whatsappCalls.iceServers(conversationId);
    pc = new RTCPeerConnection({ iceServers: ice.ice_servers });
    attachPcListeners(pc, ensureRemoteAudio());
    await pc.setRemoteDescription(
      new RTCSessionDescription({ type: 'offer', sdp: offerSdp })
    );
    localStream = await navigator.mediaDevices.getUserMedia({ audio: true });
    localStream.getTracks().forEach(t => pc.addTrack(t, localStream));
    const answer = await pc.createAnswer();
    await pc.setLocalDescription(answer);
    await waitForIceGathering(pc);
    try {
      const { data } = await whatsappCalls.accept(
        conversationId,
        recordId,
        pc.localDescription.sdp
      );
      recordingMeta = { conversationId, recordId: data.id };
      return {
        recordId: data.id,
        callId: data.wa_call_id,
        status: data.status,
      };
    } catch (e) {
      cleanup();
      const err = new Error(e.response?.data?.error || 'Accept failed');
      err.status = e.response?.status;
      throw err;
    }
  };

  const applyAnswer = async sdpAnswer => {
    if (!pc) return;
    await pc.setRemoteDescription(
      new RTCSessionDescription({ type: 'answer', sdp: sdpAnswer })
    );
  };

  const toggleMute = () => {
    isMuted.value = !isMuted.value;
    pc?.getSenders().forEach(s => {
      if (s.track && s.track.kind === 'audio') s.track.enabled = !isMuted.value;
    });
  };

  const hangup = async (conversationId, recordId) => {
    try {
      if (recordId) await whatsappCalls.terminate(conversationId, recordId);
    } finally {
      cleanup();
    }
  };

  return {
    startCall,
    answerCall,
    applyAnswer,
    toggleMute,
    hangup,
    isMuted,
    cleanup,
  };
}
